"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";

async function saveMotionPref(reduced: boolean | null) {
  await fetch("/api/user/preferences", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessibilitySettings: { reducedMotion: reduced } }),
  }).catch(() => {});
}

export function useReducedMotion() {
  // MotionProvider wraps the tree in app/layout.tsx *outside* SessionProvider.
  // useSession() returns undefined in that case (and during prerender), so
  // never destructure it.
  const session = useSession();
  const status = session?.status;
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [userPreference, setUserPreference] = useState<boolean | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    if (status === "authenticated") {
      fetch("/api/user/preferences")
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          const pref = data?.accessibilitySettings?.reducedMotion;
          if (pref !== undefined && pref !== null) {
            setUserPreference(!!pref);
          }
        })
        .catch(() => {});
    }

    const handleChange = (e: MediaQueryListEvent) => {
      if (userPreference === null) {
        setPrefersReducedMotion(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [userPreference, status]);

  const setMotionPreference = (reduced: boolean) => {
    setPrefersReducedMotion(reduced);
    setUserPreference(reduced);
    saveMotionPref(reduced);
  };

  const effectiveReducedMotion =
    userPreference !== null ? userPreference : prefersReducedMotion;

  return {
    prefersReducedMotion: effectiveReducedMotion,
    setMotionPreference,
    isSystemPreference: userPreference === null,
    resetToSystemPreference: () => {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      setPrefersReducedMotion(mediaQuery.matches);
      setUserPreference(null);
      saveMotionPref(null);
    },
  };
}
